import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import analytics from "@/lib/segment";
import { generateAndUploadVehicleImage } from "@/lib/generateVehicleImage";
import { deleteStorageFiles } from "@/lib/storageCleanup";
import { useCompany } from "./useCompany";

export interface Vehicle {
  id: string;
  name: string;
  year: number;
  make: string;
  model: string;
  trim?: string | null;
  trim_variation?: string | null;
  license_plate?: string | null;
  vin?: string | null;
  turo_vehicle_id?: string | null;
  color?: string | null;
  mileage?: number | null;
  image_url?: string | null;
  user_id: string;
  company_id?: string | null;
}

export interface VehicleFormData {
  name: string;
  year: number;
  make: string;
  model: string;
  trim?: string;
  trim_variation?: string;
  license_plate?: string;
  vin?: string;
  turo_vehicle_id?: string;
  color?: string;
  mileage?: number;
  image_url?: string | null;
}

export const useVehicles = () => {
  const queryClient = useQueryClient();
  const { company } = useCompany();

  const { data: vehicles, isLoading } = useQuery({
    queryKey: ["vehicles", company?.id],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [] as Vehicle[];

      let query = supabase
        .from("vehicles")
        .select("*")
        .order("created_at", { ascending: false });

      if (company?.id) {
        query = query.eq("company_id", company.id);
      } else {
        query = query.eq("user_id", user.id);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as Vehicle[];
    },
  });

  const addVehicle = useMutation({
    mutationFn: async (vehicleData: VehicleFormData) => {
      if (vehicleData.name.toLowerCase().trim() === "operations") {
        throw new Error('"Operations" is a reserved name and cannot be used as a vehicle name.');
      }
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("vehicles")
        .insert({
          ...vehicleData,
          user_id: user.id,
          company_id: company?.id ?? null,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ["vehicles"] });
      
      // Get updated vehicle count for analytics
      const updatedVehicles = queryClient.getQueryData<Vehicle[]>(["vehicles"]);
      const numVehicles = updatedVehicles?.length ?? 1;
      
      analytics.track("Vehicle Added", {
        vehicle_id: data.id,
        vehicle_make: data.make,
        vehicle_model: data.model,
        vehicle_year: data.year,
        num_vehicles_in_company: numVehicles,
      });
      toast.success("Vehicle added successfully");

      // Fire-and-forget: generate AI vehicle image if no image was provided
      if (!data.image_url) {
        generateAndUploadVehicleImage({
          vehicleId: data.id,
          make: data.make,
          model: data.model,
          year: data.year,
          nickname: data.name,
          color: data.color ?? undefined,
        }).then(() => {
          queryClient.invalidateQueries({ queryKey: ["vehicles"] });
        });
      }
    },
    onError: (error) => {
      toast.error("Failed to add vehicle: " + error.message);
    },
  });

  const updateVehicle = useMutation({
    mutationFn: async ({ id, ...vehicleData }: VehicleFormData & { id: string }) => {
      if (vehicleData.name && vehicleData.name.toLowerCase().trim() === "operations") {
        throw new Error('"Operations" is a reserved name and cannot be used as a vehicle name.');
      }
      const { data, error } = await supabase
        .from("vehicles")
        .update(vehicleData)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["vehicles"] });
      toast.success("Vehicle updated successfully");

      // Auto-generate AI image if vehicle has no image after update
      if (!data.image_url) {
        generateAndUploadVehicleImage({
          vehicleId: data.id,
          make: data.make,
          model: data.model,
          year: data.year,
          nickname: data.name,
          color: data.color ?? undefined,
        }).then(() => {
          queryClient.invalidateQueries({ queryKey: ["vehicles"] });
        });
      }
    },
    onError: (error) => {
      toast.error("Failed to update vehicle: " + error.message);
    },
  });

  const deleteVehicle = useMutation({
    mutationFn: async (vehicleId: string) => {
      // Fetch vehicle to get image_url for cleanup
      const { data: vehicle } = await supabase
        .from("vehicles")
        .select("image_url")
        .eq("id", vehicleId)
        .single();

      // Fetch associated expenses to delete their receipt files
      const { data: vehicleExpenses } = await supabase
        .from("expenses")
        .select("receipt_urls")
        .eq("vehicle_id", vehicleId);

      // Fetch maintenance tasks to delete their attachment files
      const { data: maintenanceTasks } = await supabase
        .from("maintenance_tasks")
        .select("attachments")
        .eq("vehicle_id", vehicleId);

      // Delete receipt files from storage
      if (vehicleExpenses) {
        const receiptUrls = vehicleExpenses.flatMap(
          (e) => (Array.isArray(e.receipt_urls) ? e.receipt_urls : []) as string[]
        );
        if (receiptUrls.length > 0) {
          await deleteStorageFiles(receiptUrls, "expense-receipts");
        }
      }

      // Delete maintenance attachment files from storage
      if (maintenanceTasks) {
        const attachmentUrls = maintenanceTasks.flatMap(
          (t) => (Array.isArray(t.attachments) ? t.attachments : []) as string[]
        );
        if (attachmentUrls.length > 0) {
          await deleteStorageFiles(attachmentUrls, "maintenance-attachments");
        }
      }

      // Delete vehicle image from storage
      if (vehicle?.image_url) {
        await deleteStorageFiles([vehicle.image_url], "vehicle-images");
      }

      // Delete associated trips
      const { error: tripsError } = await supabase
        .from("trips")
        .delete()
        .eq("vehicle_id", vehicleId);

      if (tripsError) throw tripsError;

      // Delete associated expenses
      const { error: expensesError } = await supabase
        .from("expenses")
        .delete()
        .eq("vehicle_id", vehicleId);

      if (expensesError) throw expensesError;

      // Delete associated maintenance tasks
      const { error: maintenanceError } = await supabase
        .from("maintenance_tasks")
        .delete()
        .eq("vehicle_id", vehicleId);

      if (maintenanceError) throw maintenanceError;

      // Delete the vehicle
      const { error } = await supabase
        .from("vehicles")
        .delete()
        .eq("id", vehicleId);

      if (error) throw error;
    },
    onSuccess: async (_, vehicleId) => {
      await queryClient.invalidateQueries({ queryKey: ["vehicles"] });
      queryClient.invalidateQueries({ queryKey: ["trips"] });
      queryClient.invalidateQueries({ queryKey: ["expenses"] });
      
      // Get updated vehicle count for analytics
      const updatedVehicles = queryClient.getQueryData<Vehicle[]>(["vehicles"]);
      const numVehicles = updatedVehicles?.length ?? 0;
      
      analytics.track("Vehicle Deleted", {
        vehicle_id: vehicleId,
        num_vehicles_in_company: numVehicles,
      });
      toast.success("Vehicle deleted successfully");
    },
    onError: (error) => {
      toast.error("Failed to delete vehicle: " + error.message);
    },
  });

  const mergeVehicles = useMutation({
    mutationFn: async ({ sourceId, destinationId }: { sourceId: string; destinationId: string }) => {
      // Fetch both vehicles
      const { data: sourceVehicle, error: srcErr } = await supabase
        .from("vehicles")
        .select("*")
        .eq("id", sourceId)
        .single();

      if (srcErr) throw srcErr;

      const { data: destVehicle, error: destErr } = await supabase
        .from("vehicles")
        .select("*")
        .eq("id", destinationId)
        .single();

      if (destErr) throw destErr;

      // Merge fields: destination takes priority, fill empty fields from source
      const mergedData: Record<string, unknown> = {};
      const fieldsToMerge = [
        'name', 'year', 'make', 'model', 'trim', 'trim_variation', 
        'license_plate', 'vin', 'turo_vehicle_id', 'color', 'mileage', 'image_url'
      ] as const;

      for (const field of fieldsToMerge) {
        const destValue = destVehicle[field];
        const srcValue = sourceVehicle[field];
        // Keep destination value if it exists, otherwise take source
        if (destValue === null || destValue === undefined || destValue === '') {
          if (srcValue !== null && srcValue !== undefined && srcValue !== '') {
            mergedData[field] = srcValue;
          }
        }
      }

      // Update destination vehicle with merged data if there are any changes
      if (Object.keys(mergedData).length > 0) {
        const { error: updateErr } = await supabase
          .from("vehicles")
          .update(mergedData)
          .eq("id", destinationId);

        if (updateErr) throw updateErr;
      }

      // Transfer trips from source to destination
      const { error: tripsErr } = await supabase
        .from("trips")
        .update({ vehicle_id: destinationId })
        .eq("vehicle_id", sourceId);

      if (tripsErr) throw tripsErr;

      // Transfer expenses from source to destination
      const { error: expensesErr } = await supabase
        .from("expenses")
        .update({ vehicle_id: destinationId })
        .eq("vehicle_id", sourceId);

      if (expensesErr) throw expensesErr;

      // Delete source vehicle
      const { error: deleteErr } = await supabase
        .from("vehicles")
        .delete()
        .eq("id", sourceId);

      if (deleteErr) throw deleteErr;

      return { sourceId, destinationId };
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ["vehicles"] });
      queryClient.invalidateQueries({ queryKey: ["trips"] });
      queryClient.invalidateQueries({ queryKey: ["expenses"] });
      
      // Get updated vehicle count for analytics
      const updatedVehicles = queryClient.getQueryData<Vehicle[]>(["vehicles"]);
      const numVehicles = updatedVehicles?.length ?? 0;
      
      analytics.track("Vehicles Merged", {
        source_vehicle_id: result.sourceId,
        destination_vehicle_id: result.destinationId,
        num_vehicles_in_company: numVehicles,
      });
      toast.success("Vehicles merged successfully");
    },
    onError: (error) => {
      toast.error("Failed to merge vehicles: " + error.message);
    },
  });

  return {
    vehicles,
    isLoading,
    addVehicle,
    updateVehicle,
    deleteVehicle,
    mergeVehicles,
  };
};
